import { pollSurfImageJob } from "./surfImageJob.js";
import { applySurfJobToDeck } from "./surfJobAppliers.js";
import { submitGenerateImageForLayer, submitGenerateRasterForTextLayer, submitGenerateSlideVisual, isSurfJobSubmitResult, } from "./surfJobSubmits.js";
import { getTextRenderMode } from "../illustration/textRaster.js";
import { artDirectionPromptLines } from "../style/artDirection.js";
import { fetchS3KeyAsDataUrl } from "./imageDataUrl.js";
import { setSlideBackgroundSaliency, tryAnalyzeSlideSaliency, } from "./slideSaliencyService.js";
export function composeContextualImagePrompt(style, subject, mode, slideIndex) {
    const safe = style.legibility.safeZones[0];
    const zone = safe
        ? `Calm negative space for text at x=${safe.x.toFixed(2)} y=${safe.y.toFixed(2)} w=${safe.w.toFixed(2)} h=${safe.h.toFixed(2)}.`
        : "Leave calm negative space for text.";
    return [
        mode === "background" ? "16:9 presentation slide BACKGROUND only." : "16:9 illustrative CONTENT image.",
        "LOOK-ONLY — no text, logos, readable typography, charts, numbers, or data.",
        `Subject (authoritative): ${subject}`,
        `Visual treatment: ${style.backgroundTreatment}`,
        `Palette mood: surface ${style.palette.surface}, accent ${style.palette.accent}`,
        ...artDirectionPromptLines(style, mode),
        zone,
        `Slide ${slideIndex + 1}.`,
    ].join(" ");
}
/** Resolve image subject for a layer (F13 manual prompt or contextual fallback). */
export function resolveImageLayerSubject(slide, opts) {
    const manual = opts?.subject?.trim();
    if (manual)
        return manual;
    const fromBrief = slide.visualBrief?.subject?.trim();
    if (fromBrief)
        return fromBrief;
    const titleLayer = slide.layers.find((l) => l.type === "text" && l.textRole === "title");
    const fromTitle = titleLayer?.content?.trim();
    if (fromTitle)
        return fromTitle;
    const anyText = slide.layers.find((l) => l.type === "text" && l.content?.trim());
    if (anyText?.content?.trim())
        return anyText.content.trim();
    return "abstract conceptual illustration";
}
export function getLayerCutout(layer) {
    return layer.style?.cutout === true;
}
/** Generate image for a single image widget (F13). */
export async function generateImageForLayer(cfg, deck, slideId, layerId, opts) {
    const { submit, deck: prepared } = await submitGenerateImageForLayer(cfg, deck, slideId, layerId, opts);
    const outputAssetKey = await pollSurfImageJob(cfg, submit.jobId);
    return applySurfJobToDeck(prepared, {
        op: submit.op,
        target: submit.target,
        output: { kind: "asset", assetKey: outputAssetKey },
        applyMeta: submit.applyMeta,
    });
}
export async function generateSlideVisual(cfg, deck, slideIndex) {
    const submitted = await submitGenerateSlideVisual(cfg, deck, slideIndex);
    if (!isSurfJobSubmitResult(submitted))
        return submitted.deck;
    const { submit, deck: prepared } = submitted;
    const outputAssetKey = await pollSurfImageJob(cfg, submit.jobId);
    let resultDeck = applySurfJobToDeck(prepared, {
        op: submit.op,
        target: submit.target,
        output: { kind: "asset", assetKey: outputAssetKey },
        applyMeta: submit.applyMeta,
    });
    const slideId = submit.target.slideId;
    if (slideId && cfg.presignedBaseUrl) {
        try {
            const dataUrl = await fetchS3KeyAsDataUrl(cfg, outputAssetKey);
            const zones = await tryAnalyzeSlideSaliency(cfg, dataUrl);
            if (zones) {
                resultDeck = setSlideBackgroundSaliency(resultDeck, slideId, zones);
            }
        }
        catch {
            /* saliency best-effort */
        }
    }
    return resultDeck;
}
/** FIFO contextual images (brief + generate per slide). */
export async function generateAllSlideVisuals(cfg, deck, onProgress) {
    let nextDeck = deck;
    const total = deck.slides.length;
    for (let index = 0; index < total; index += 1) {
        const slide = nextDeck.slides[index];
        const manualColor = slide.background.solidColor?.trim();
        if (manualColor &&
            /^#[0-9A-Fa-f]{6}$/i.test(manualColor) &&
            !slide.background.s3Key?.trim()) {
            onProgress?.({ index, total, slideId: slide.id, status: "done" });
            continue;
        }
        onProgress?.({ index, total, slideId: slide.id, status: "generating" });
        try {
            nextDeck = await generateSlideVisual(cfg, nextDeck, index);
            onProgress?.({ index, total, slideId: slide.id, status: "done" });
        }
        catch (e) {
            const message = e instanceof Error ? e.message : String(e);
            onProgress?.({ index, total, slideId: slide.id, status: "error", message });
            throw e;
        }
    }
    return nextDeck;
}
/** U9 — generate baked illustration for a text widget (content preserved). */
export async function generateRasterForTextLayer(cfg, deck, slideId, layerId, hooks) {
    const { submit, deck: prepared } = await submitGenerateRasterForTextLayer(cfg, deck, slideId, layerId, hooks);
    const outputAssetKey = await pollSurfImageJob(cfg, submit.jobId);
    return applySurfJobToDeck(prepared, {
        op: submit.op,
        target: submit.target,
        output: { kind: "asset", assetKey: outputAssetKey },
        applyMeta: submit.applyMeta,
    });
}
/** FIFO regenerate for all text layers in raster mode. */
export async function regenerateAllTextRasters(cfg, deck, onProgress) {
    const targets = [];
    for (const slide of deck.slides) {
        for (const layer of slide.layers) {
            if (layer.type === "text" && getTextRenderMode(layer) === "raster") {
                targets.push({ slideId: slide.id, layerId: layer.id });
            }
        }
    }
    let nextDeck = deck;
    const total = targets.length;
    for (let index = 0; index < total; index += 1) {
        const { slideId, layerId } = targets[index];
        onProgress?.({ index, total, slideId, layerId, status: "generating" });
        try {
            nextDeck = await generateRasterForTextLayer(cfg, nextDeck, slideId, layerId);
            onProgress?.({ index, total, slideId, layerId, status: "done" });
        }
        catch (e) {
            const message = e instanceof Error ? e.message : String(e);
            onProgress?.({ index, total, slideId, layerId, status: "error", message });
            throw e;
        }
    }
    return nextDeck;
}
